import React, { useContext, useMemo } from "react"
import {
    Alert,
    Autocomplete,
    Avatar,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    TextField,
} from "@mui/material"
import { Cancel } from "@mui/icons-material"
import { DataGrid } from "@mui/x-data-grid"
import { useQuery } from "react-query"
import { find, reject } from "lodash"

import isPhone from "../../Utils/isPhone.js"
import useAddMembers from "../../Hooks/useAddMembers.js"
import AuthContext from "../../Context/auth_provider.jsx"
import useAxiosPrivate from "../../Hooks/useAxiosPrivate.js"
import { RP_ASSETS_URL, SSO_API_BASE_URL } from "../../Utils/config.js"

const userCol = [
    {
        field: "profile_picture",
        headerName: "Photo",
        headerClassName: "white-header",
        flex: 1,
        sortable: false,
    },
    {
        field: "name",
        headerName: "Name",
        headerClassName: "white-header",
        flex: 3,
        sortable: false,
        headerAlign: "center",
        align: "center",
    },
    {
        field: "phone",
        headerName: "Phone",
        headerClassName: "white-header",
        flex: 2,
        sortable: false,
        headerAlign: "center",
        align: "center",
    },
    {
        field: "programs",
        headerName: "Programs",
        headerClassName: "white-header",
        flex: 4,
        sortable: false,
        headerAlign: "center",
        align: "center",
    },
    {
        field: "action",
        headerName: "Action",
        headerClassName: "white-header",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
    },
]

function AddBulkMember({ file, setFile, close }) {
    const axiosPrivate = useAxiosPrivate()
    const { companyId } = useContext(AuthContext)

    const {
        assignUser,
        clearAssign,
        requestAssign,
        isLoading: assigning,
    } = useAddMembers()

    const {
        data: users,
        isError,
        error,
        isLoading,
    } = useQuery(
        ["trip_users"],
        () =>
            axiosPrivate
                .post(`corporate/${companyId}/users/phone`, {
                    phones: file
                        .filter((ele) => !!ele?.phone)
                        .map((el) => el.phone),
                })
                .then((res) => res.data),
        {
            retry: false,
        }
    )

    const combinedArray = useMemo(
        () =>
            file?.map((ele) => {
                const registeredUser = find(users?.data, (user) =>
                    isPhone(user.phone, ele.phone)
                )
                if (registeredUser) {
                    // ADD USER TO BULK ASSIGN FORM
                    assignUser({
                        userId: registeredUser.id,
                        programs: ele.programs,
                    })
                    return {
                        ...ele,
                        name: `${registeredUser.first_name} ${registeredUser.last_name}`,
                    }
                }
                return ele
            }),
        [assignUser, file, users?.data]
    )

    const handleCancel = (data) => {
        clearAssign()
        const removed = reject(file, (ele) => ele.phone === data.phone)
        setFile(removed)
    }

    const colFields = userCol.map((ele) => {
        if (ele.field === "programs") {
            return {
                ...ele,
                width: 300,
                renderCell: (params) => (
                    <Autocomplete
                        readOnly
                        multiple
                        fullWidth
                        value={params.row?.programs
                            ?.split(",")
                            .map((el) => el.trim())}
                        options={[]}
                        renderInput={(paras) => <TextField {...paras} />}
                        sx={{
                            "& .MuiChip-label": {
                                color: "secondary.main",
                                fontWeight: "bold",
                                letterSpacing: ".1em",
                            },
                            "& .MuiChip-root": {
                                backgroundColor: "white",
                            },
                            "& .MuiSvgIcon-root": {
                                display: "none",
                            },
                        }}
                    />
                ),
            }
        }
        if (ele.field === "action") {
            return {
                ...ele,
                renderCell: (params) => (
                    <IconButton onClick={() => handleCancel(params?.row)}>
                        <Cancel />
                    </IconButton>
                ),
            }
        }
        if (ele.field === "profilePic") {
            return {
                ...ele,
                renderCell: (params) => (
                    <Avatar
                        sx={{
                            height: "100%",
                            maxHeight: "40px",
                            borderRadius: "50%",
                            objectFit: "contain",
                        }}
                        src={
                            `${SSO_API_BASE_URL}/v1/${RP_ASSETS_URL}/${
                                find(users?.data, (el) =>
                                    isPhone(el.phone, params.row.phone)
                                )?.profile_picture
                            }` || ""
                        }
                    />
                ),
            }
        }
        return ele
    })

    if (isLoading)
        return (
            <Box>
                <CircularProgress />
            </Box>
        )
    if (isError) {
        return (
            <Alert
                severity="error"
                sx={{ py: 2, pl: 3 }}
                onClose={() => close()}
            >
                {error.response?.data?.error?.field_error?.map(
                    (err) =>
                        `${err.name} at line ${
                            err.description
                                ? Number(err.description.split(":")[0]) + 1
                                : null
                        } is invalid`
                )}
            </Alert>
        )
    }

    return (
        <Box
            sx={{
                marginTop: file ? "1em" : null,
                "& .white-header": {
                    backgroundColor: "white",
                },
            }}
        >
            <Box sx={{ height: "250px" }}>
                <DataGrid
                    columns={colFields}
                    rows={combinedArray.filter((el) => el.phone) || []}
                    hideFooter
                    disableColumnMenu
                    disableSelectionOnClick
                    getRowId={(params) => params.phone}
                    getRowHeight={({ id, densityFactor }) => {
                        if (id % 2 === 0) {
                            return 50 * densityFactor
                        }

                        return null
                    }}
                    sx={{
                        backgroundColor: "#fff",
                        boxShadow: 2,
                        border: 2,
                        borderColor: "primary.main",
                        "& .MuiDataGrid-cell:hover": {
                            color: "primary.main",
                        },
                    }}
                />
            </Box>
            <Divider sx={{ my: "1em" }} />
            <Box
                sx={{
                    display: "flex",
                    gap: "1em",
                    pr: "2em",
                    justifyContent: "flex-end",
                }}
            >
                <Button
                    size="small"
                    variant="contained"
                    sx={{
                        ":hover": { bgcolor: "#e4e4e4" },
                        backgroundColor: "#e4e4e4",
                        textTransform: "none",
                        fontWeight: "bold",
                        width: 120,
                    }}
                    onClick={() => close()}
                >
                    Cancel
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    sx={{
                        ":hover": { bgcolor: "primary.main" },
                        backgroundColor: "primary.main",
                        textTransform: "none",
                        fontWeight: "bold",
                        width: 120,
                    }}
                    startIcon={
                        assigning ? (
                            <CircularProgress
                                size="1em"
                                sx={{ color: "white" }}
                            />
                        ) : null
                    }
                    onClick={() => requestAssign(() => close())}
                >
                    Confirm
                </Button>
            </Box>
        </Box>
    )
}

export default AddBulkMember
